import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TechnicalCapabilityPanel = ({ data, onChange }) => {
  const proficiencyLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const handleFieldChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleCertificationChange = (index, field, value) => {
    const updatedCertifications = [...data?.certifications];
    updatedCertifications[index] = { ...updatedCertifications?.[index], [field]: value };
    handleFieldChange('certifications', updatedCertifications);
  };

  const addCertification = () => {
    handleFieldChange('certifications', [
      ...data?.certifications,
      { name: '', issuer: '', expiryDate: '', status: 'active' }
    ]);
  };

  const removeCertification = (index) => {
    const updatedCertifications = data?.certifications?.filter((_, i) => i !== index);
    handleFieldChange('certifications', updatedCertifications);
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center gap-12">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Wrench" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground font-heading">
              Technical Capability Assessment
            </h2>
            <p className="text-sm text-muted-foreground caption mt-2">
              Evaluate technical resources and infrastructure capabilities
            </p>
          </div>
        </div>
      </div>
      <div className="p-16 md:p-20 lg:p-24 space-y-20 md:space-y-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
          <Input
            label="Certified Engineers"
            type="number"
            placeholder="Enter number of certified engineers"
            value={data?.certifiedEngineers}
            onChange={(e) => handleFieldChange('certifiedEngineers', e?.target?.value)}
            required
          />

          <Select
            label="Integration Skills Level"
            options={proficiencyLevels}
            value={data?.integrationSkills}
            onChange={(value) => handleFieldChange('integrationSkills', value)}
            placeholder="Select proficiency level"
            required
          />
        </div>

        <div className="space-y-12">
          <label className="text-sm font-medium text-foreground">
            Infrastructure Capabilities
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            <Checkbox
              label="Lab Availability"
              checked={data?.hasLab}
              onChange={(e) => handleFieldChange('hasLab', e?.target?.checked)}
            />
            <Checkbox
              label="Demo Capability"
              checked={data?.hasDemo}
              onChange={(e) => handleFieldChange('hasDemo', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="space-y-16">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">
              Certifications
            </label>
            <button
              onClick={addCertification}
              className="flex items-center gap-8 px-12 py-8 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-smooth"
            >
              <Icon name="Plus" size={16} />
              Add Certification
            </button>
          </div>

          <div className="space-y-12">
            {data?.certifications?.map((cert, index) => (
              <div
                key={index}
                className={`
                  p-16 rounded-lg border transition-smooth
                  ${isExpired(cert?.expiryDate) ? 'border-error bg-error/5' : ''}
                  ${isExpiringSoon(cert?.expiryDate) ? 'border-warning bg-warning/5' : ''}
                  ${!isExpired(cert?.expiryDate) && !isExpiringSoon(cert?.expiryDate) ? 'border-border bg-muted/30' : ''}
                `}
              >
                <div className="flex items-start justify-between gap-12 mb-12">
                  <div className="flex items-center gap-8">
                    <Icon
                      name={isExpired(cert?.expiryDate) ? 'AlertCircle' : isExpiringSoon(cert?.expiryDate) ? 'AlertTriangle' : 'Award'}
                      size={18}
                      className={`
                        ${isExpired(cert?.expiryDate) ? 'text-error' : ''}
                        ${isExpiringSoon(cert?.expiryDate) ? 'text-warning' : ''}
                        ${!isExpired(cert?.expiryDate) && !isExpiringSoon(cert?.expiryDate) ? 'text-success' : ''}
                      `}
                    />
                    <span className="text-sm font-medium text-foreground">
                      Certification {index + 1}
                    </span>
                  </div>
                  <button
                    onClick={() => removeCertification(index)}
                    className="text-muted-foreground hover:text-error transition-smooth"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <Input
                    label="Certification Name"
                    type="text"
                    placeholder="e.g., AWS Solutions Architect"
                    value={cert?.name}
                    onChange={(e) => handleCertificationChange(index, 'name', e?.target?.value)}
                    required
                  />
                  <Input
                    label="Issuing Organization"
                    type="text"
                    placeholder="e.g., Amazon Web Services"
                    value={cert?.issuer}
                    onChange={(e) => handleCertificationChange(index, 'issuer', e?.target?.value)}
                    required
                  />
                  <Input
                    label="Expiry Date"
                    type="date"
                    value={cert?.expiryDate}
                    onChange={(e) => handleCertificationChange(index, 'expiryDate', e?.target?.value)}
                    required
                  />
                  <Select
                    label="Status"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'expired', label: 'Expired' },
                      { value: 'pending', label: 'Pending Renewal' }
                    ]}
                    value={cert?.status}
                    onChange={(value) => handleCertificationChange(index, 'status', value)}
                  />
                </div>

                {isExpired(cert?.expiryDate) && (
                  <div className="mt-12 flex items-center gap-8 text-error text-sm">
                    <Icon name="AlertCircle" size={16} />
                    <span>This certification has expired</span>
                  </div>
                )}

                {isExpiringSoon(cert?.expiryDate) && !isExpired(cert?.expiryDate) && (
                  <div className="mt-12 flex items-center gap-8 text-warning text-sm">
                    <Icon name="AlertTriangle" size={16} />
                    <span>Expires within 30 days - renewal required</span>
                  </div>
                )}
              </div>
            ))}

            {data?.certifications?.length === 0 && (
              <div className="text-center py-24 text-muted-foreground">
                <Icon name="Award" size={48} className="mx-auto mb-12 opacity-50" />
                <p className="text-sm">No certifications added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalCapabilityPanel;